import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '@economic/taco';
import { refreshCurrentLegacyPage, useCurrentAgreement } from '@economic/react-app';
import { t } from '@lingui/macro';
import {
    BulkCustomerLedgerCardsSendingInformation,
    BulkCustomerLedgerCardsSendingRequest,
    BulkCustomerLedgerCardsSendingResponse,
} from '../../../../../../types/internal-api';
import { InvoiceLayoutType } from '../../../../../../types/internal-api-extra';
import internalApiClient from '../../../../../shared/util/api/internalApiClient';
import { format } from '../../../../../shared/util/utilities/date';
import useQuery from '../../../../../shared/hooks/useQuery';
import { t as tLegacy, tBB, useLocalization } from '../../../../../shared/components/l10n/l10n';
import { useEmailTemplates } from '../../../invoicing/invoices/utils/useEmailTemplates';
import { emailOptionsFromTemplates } from '../../../invoicing/invoices/utils/emailOptionsFromTemplates';
import { emailTemplateFormState } from '../../../invoicing/invoices/utils/emailTemplateFormState';
import { CustomerObject, SendBulkCustomerLedgerCardsDialogFormData, SendBulkCustomerLedgerCardsDialogRouteState } from '../types';
import { InvoicingMessage, InvoicingMessageType } from '../../../invoicing/invoices/book-and-send/types';
import { SendBulkCustomerLedgerCardsDialog } from './SendBulkCustomerLedgerCardsDialog';
import { AddPeriod, CustomerLedgerCardsFilterState } from '../../../../reports/customers/ledger-cards/types/types';
import { buildPeriodText, showStatementsOfAccounts } from '../../../../reports/customers/ledger-cards/utils/utils';

export interface SendBulkCustomerLedgerCardsDialogContainerProps {
    openNewTab?: boolean;
    customerIds?: number[];
    customerEmails?: CustomerObject[];
    filterState?: CustomerLedgerCardsFilterState;
    open?: boolean;
    onClose?: () => void;
}

function SendBulkCustomerLedgerCardsDialogContainer({
    openNewTab = false,
    ...props
}: SendBulkCustomerLedgerCardsDialogContainerProps) {
    const hasValidProps = !!props.customerIds && !!props.filterState;
    const toast = useToast();
    const agreement = useCurrentAgreement();
    const location = useLocation();
    const navigate = useNavigate();
    const { state: routeState } = location as { state: SendBulkCustomerLedgerCardsDialogRouteState };

    let customerIds = '';

    if (hasValidProps) {
        if (props.customerIds?.length) {
            customerIds = props.customerIds.map(id => `ids=${id}`).join('&');
        }
    } else {
        const ids = new URLSearchParams();
        routeState?.customerIds.forEach(id => ids.append('ids', id.toString()));
        customerIds = ids.toString();
    }

    const { loading: loadingInformation, data } = useQuery<BulkCustomerLedgerCardsSendingInformation>(
        `/sales/customer-ledger-cards/bulk-sending-information?${customerIds}`,
        null,
        {
            skip: hasValidProps ? !(props?.customerIds && props.customerIds.length > 0) : !(routeState?.customerIds?.length > 0),
        }
    );

    const loadingTexts = useLocalization(textIds);
    const { data: templates, loading: loadingTemplates } = useEmailTemplates(InvoiceLayoutType.AccountStatement);

    const [sending, setSending] = useState<boolean>(false);
    const [openDialog, setOpenDialog] = useState<boolean>(props.open ?? false);

    const transformedInformation = useMemo<SendBulkCustomerLedgerCardsDialogFormData | undefined>(() => {
        if (!data) {
            return undefined;
        }

        return {
            validCustomerIds: data.validCustomerIds,
            customersWithInvalidEmails: data.customersWithInvalidEmails,
            exceededSendingLimit: data.exceededSendingLimit,
            emailOptions: {
                ...data,
                showPdf: data.showAfterSending,
                saveSubject: false,
                saveBody: false,
                ...emailOptionsFromTemplates(templates),
            },

            ...emailTemplateFormState(templates),
        };
    }, [data, templates]);

    const messages = useMemo<InvoicingMessage[]>(() => {
        if (loadingTexts || !data?.customersWithInvalidEmails || data.customersWithInvalidEmails.length === 0) {
            return [];
        }

        const messages: InvoicingMessage[] = [];
        if (data?.customersWithInvalidEmails?.length <= 3) {
            const customersWithInvalidEmailsWarning: string = tBB(
                'FORM_BOOKANDSEND_BULKCUSTOMERLEDGERCARD_WARNING_INVALIDCUSTOMEREMAILS',
                {
                    CUSTOMERS: (
                        <ul>
                            {data.customersWithInvalidEmails.map(c => (
                                <li key={c.customerNumber}>
                                    {tLegacy(
                                        'FORM_BOOKANDSEND_BULKCUSTOMERLEDGERCARD_WARNING_INVALIDCUSTOMEREMAILS_CUSTOMERDETAILS',
                                        {
                                            ID: c.customerNumber,
                                            NAME: c.name,
                                        }
                                    )}
                                </li>
                            ))}
                        </ul>
                    ),
                }
            );
            messages.push({
                message: customersWithInvalidEmailsWarning,
                type: InvoicingMessageType.Warning,
            });
            return messages;
        }
        const customersWithInvalidEmailCount = data.customersWithInvalidEmails.length;
        const allCustomersCount = routeState?.customerIds.length;
        messages.push({
            message: t`No valid invoice e-mail has been entered for ${customersWithInvalidEmailCount} out of ${allCustomersCount} customers. Please note that the customers will not receive any statement of accounts.`,
            type: InvoicingMessageType.Warning,
        });

        return messages;
    }, [data, loadingTexts, routeState?.customerIds.length]);

    async function send(values: SendBulkCustomerLedgerCardsDialogFormData) {
        try {
            setSending(true);

            const periodText = props.filterState ? buildPeriodText(props.filterState) : '';
            const getText = (filterState: CustomerLedgerCardsFilterState, addPeriodText: AddPeriod, filterStateText: string) => {
                return filterState.addPeriodTo === addPeriodText ? `${periodText} ${filterStateText}` : filterStateText;
            };

            const request: BulkCustomerLedgerCardsSendingRequest = {
                customerIds: values.validCustomerIds,
                heading: props.filterState
                    ? getText(props.filterState, AddPeriod.Heading, props.filterState.heading ?? '')
                    : routeState.heading,
                text: props.filterState
                    ? getText(props.filterState, AddPeriod.TextField, props.filterState.text ?? '')
                    : routeState.text,
                currency: props.filterState ? props.filterState.currency ?? '' : routeState.currency,
                fromDate: props.filterState ? format(props.filterState.fromDate, 'yyyy-mm-dd') ?? '' : routeState.fromDate,
                toDate: props.filterState ? format(props.filterState.toDate, 'yyyy-mm-dd') ?? '' : routeState.toDate,
                onlyOpen: props.filterState ? props.filterState.showOpenOnly : routeState.onlyOpen,
                layoutGroup: props.filterState ? props.filterState.layoutGroup : routeState.layoutGroup,
                subject: values.emailOptions.subject,
                body: values.emailOptions.body,
                encrypt: values.emailOptions.encrypt,
                includePdfLink: values.emailOptions.includePdfLink,
                includePdfAsAttachment: values.emailOptions.includePdfAsAttachment,
                saveSubject: values.emailOptions.saveSubject,
                saveBody: values.emailOptions.saveBody,
                showAfterSending: values.emailOptions.showPdf,
            };

            request.currency = request.currency === 'Converted' ? 'omregnet' : request.currency;

            if (values.emailOptions.showPdf && openNewTab) {
                const customerNumbers = request.customerIds.join(',');
                const customersWithValidEmails =
                    props.customerEmails
                        ?.filter(customer => !values.customersWithInvalidEmails.some(c => c.customerNumber === customer.number))
                        .map(customer => customer.email)
                        .join(',') ?? '';

                const url = showStatementsOfAccounts(customerNumbers, customersWithValidEmails, agreement, props.filterState);
                window.top?.firebirdTask.then(() => window.top?.PopUp2(url, 500, 600));
            }

            const result = await internalApiClient.post<BulkCustomerLedgerCardsSendingResponse>(
                `/sales/customer-ledger-cards/send-bulk`,
                request
            );

            setSending(false);
            toast.success(tLegacy`FORM_BOOKANDSEND_CUSTOMERLEDGERCARD_TOAST_SUCCESS`);

            if (values.emailOptions.showPdf && result.data.pdfLink && !openNewTab) {
                window.top?.firebirdTask.then(() => window.top?.PopUp2(result.data.pdfLink, 500, 600));
            }

            if (!hasValidProps) {
                refreshContentFrameAfterSuccess();
            }

            hide();
        } catch (error) {
            console.log(error);
        }
    }

    function refreshContentFrameAfterSuccess() {
        refreshCurrentLegacyPage('content');
    }

    function hide() {
        navigate(location.pathname + location.search);
        setOpenDialog(false);
        if (props?.onClose) {
            props.onClose();
        }
    }

    if (!hasValidProps && !routeState?.customerIds) {
        return null;
    }

    return (
        <SendBulkCustomerLedgerCardsDialog
            open={hasValidProps ? openDialog : location.hash === '#send-bulk-ledger-cards'}
            loading={loadingInformation || loadingTexts || loadingTemplates || sending}
            messages={messages}
            information={transformedInformation}
            onSend={send}
            onHide={hide}
        />
    );
}

const textIds = [
    'FORM_BOOKANDSEND_CUSTOMERLEDGERCARD_ERRORS_HEADER_CANNOTSEND',
    'FORM_BOOKANDSEND_BULKCUSTOMERLEDGERCARD_HEADER',
    'FORM_BOOKANDSEND_CUSTOMERLEDGERCARD_SINGLE_TEXT',
    'FORM_BOOKANDSEND_CUSTOMERLEDGERCARD_SEND',
    'FORM_BOOKANDSEND_CUSTOMERLEDGERCARD_TOAST_SUCCESS',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_OPEN',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_CLOSE',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_RECIPIENTS',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_TOGGLE_CC_BCC',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_CC',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_BCC',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_SUBJECT',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_BODY',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_REMEMBER',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_OPEN',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_CLOSE',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_PDF_ATTACHMENT',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_ENCRYPT',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_PDF_LINK',
    'FORM_BOOKANDSEND_BOOK_TEXT',
    'FORM_BOOKANDSEND_SELECT_ATTACHMENT_ERROR',
    'FORM_BOOKANDSEND_SELECT_EMAIL_RECIPIENT_ERROR',
    'FORM_BOOKANDSEND_BULKCUSTOMERLEDGERCARD_SHOWAFTERSENDING',
    'FORM_BOOKANDSEND_ERRORS_UNKNOWN_ERROR',
    'FORM_BOOKANDSEND_BULKCUSTOMERLEDGERCARD_WARNING_INVALIDCUSTOMEREMAILS',
    'FORM_BOOKANDSEND_BULKCUSTOMERLEDGERCARD_WARNING_INVALIDCUSTOMEREMAILS_CUSTOMERDETAILS',
];

export default SendBulkCustomerLedgerCardsDialogContainer;
